
import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CardFooter } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Mail } from "lucide-react";
import { IconInput } from "./IconInput";
import { PasswordInput } from "./PasswordInput";
import { SubmitButton } from "./SubmitButton";

export function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, user, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Get the path the user was trying to access before being redirected to login
  const from = location.state?.from?.pathname || "/dashboard";

  // Effect to handle redirection when user state changes
  useEffect(() => {
    if (user && session) {
      // User is authenticated, redirect to dashboard or previous page
      navigate(from, { replace: true });
    }
  }, [user, session, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(email, password);
      toast({
        title: "Login bem-sucedido",
        description: "Você está sendo redirecionado...",
      });
      // The useEffect above will handle the redirection once the user state updates
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: "Erro de login",
        description: "Email ou senha inválidos. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6 mx-auto">
        <IconInput
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          required
          disabled={isLoading}
          Icon={Mail}
        />
        
        <PasswordInput
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          showForgotPassword={true}
        />

        <SubmitButton
          isLoading={isLoading}
          text="Entrar"
          loadingText="Entrando..."
        />
      </form>
      
      <CardFooter className="text-center text-sm text-gray-600 justify-center pt-6 pb-0 px-0 mt-4">
        <p className="w-full text-center">
          Não tem uma conta?{" "}
          <Link to="/signup" className="text-brand-teal hover:underline font-medium transition">
            Criar conta
          </Link>
        </p>
      </CardFooter>
    </>
  );
}
